/**
 * Sprint Management View
 * Full sprint management experience (header, board/overview/insights/burndown/backlog tabs,
 * create + complete dialogs). Shared by the routed SprintManagement page and by SprintBoard's
 * inline "Sprints" tab so there is a single implementation instead of two divergent ones.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Button,
  Fab,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import InsightsIcon from '@mui/icons-material/Insights';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { usePermissions } from '../../hooks/usePermissions';
import {
  fetchSprints,
  fetchSprint,
  fetchActiveSprint,
  fetchSprintTasks,
} from '../../store/sprintSlice';
import { fetchTasks } from '../../store/taskSlice';

// Component imports
import SprintHeader from '../../components/Sprint/SprintHeader';
import SprintCreationModal from '../../components/Sprint/SprintCreationModal';
import SprintKanban from '../../components/Sprint/SprintKanban';
import BurndownChart from '../../components/Sprint/BurndownChart';
import CompleteSprintDialog from '../../components/Sprint/CompleteSprintDialog';
import SprintOverviewCard from '../../components/Sprint/SprintOverviewCard';
import AgileInsights from '../../components/Sprint/AgileInsights';
import SprintBacklog from '../../components/Sprint/SprintBacklog';

import './SprintManagement.scss';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      className="tab-panel"
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

interface SprintManagementViewProps {
  boardId: string;
  sprintId?: string;
}

const SprintManagementView: React.FC<SprintManagementViewProps> = ({ boardId, sprintId }) => {
  const dispatch = useAppDispatch();
  const { currentSprint, activeSprint, sprints } = useAppSelector((state) => state.sprints);
  const { currentBoard } = useAppSelector((state) => state.boards);
  // Mirrors the backend's BOARD_MANAGE gate now enforced on sprint
  // create/update/delete/start/complete/reset - hides actions a non-manager
  // would otherwise click into a 403 for, rather than being the actual
  // access control (the backend still enforces that).
  const { canManageBoard } = usePermissions(currentBoard?.workspaceId);

  const [currentTab, setCurrentTab] = useState(0);
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [incompleteTasks, setIncompleteTasks] = useState<any[]>([]);

  // Determine which sprint to display:
  // 1. URL sprintId if specified
  // 2. User-selected sprint from dropdown if chosen
  // 3. activeSprint if exists
  // 4. Fallback to latest sprint in board (e.g. newly created planned sprint)
  const targetId = sprintId || selectedSprintId;
  const displaySprint =
    (targetId ? sprints.find((s) => String(s.id) === String(targetId)) || currentSprint : null) ||
    activeSprint ||
    (sprints.length > 0 ? sprints[0] : null);

  useEffect(() => {
    // Fetch sprints for the board
    dispatch(fetchSprints(boardId));
    dispatch(fetchActiveSprint(boardId));

    if (sprintId) {
      dispatch(fetchSprint(sprintId));
    }

    // This view is also reachable directly (routed SprintManagement page,
    // deep link, refresh) without any ancestor having already scoped
    // state.tasks.tasks to this board - without this, backlog task lists
    // here (SprintCreationModal, SprintBacklog) fall back to whatever
    // board's tasks happen to still be in the store from a previous page.
    dispatch(fetchTasks(boardId));
  }, [boardId, sprintId, dispatch]);

  useEffect(() => {
    if (displaySprint?.id) {
      dispatch(fetchSprintTasks(displaySprint.id));
    }
  }, [displaySprint?.id, dispatch]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleCreateSprint = () => {
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const { tasks } = useAppSelector((state) => state.tasks);

  const handleCompleteClick = () => {
    if (displaySprint?.id) {
      const sprintTasks = tasks.filter((t) => String(t.sprintId) === String(displaySprint.id));
      const incomplete = sprintTasks.filter((t) => (t.status as string) !== 'done' && (t.status as string) !== 'DONE');
      setIncompleteTasks(incomplete);
    } else {
      setIncompleteTasks([]);
    }
    setCompleteDialogOpen(true);
  };

  const handleCloseCompleteDialog = () => {
    setCompleteDialogOpen(false);
  };

  const handleViewInsights = () => {
    setCurrentTab(2); // Switch to Insights tab
  };

  return (
    <Box className="sprint-management">
      <Container maxWidth="xl">
        {/* Sprint Header */}
        {displaySprint && (
          <SprintHeader
            sprint={displaySprint}
            allSprints={sprints}
            canManage={canManageBoard}
            onSelectSprint={(id) => setSelectedSprintId(id)}
            onCompleteClick={handleCompleteClick}
            onViewInsights={handleViewInsights}
          />
        )}

        {/* No Active Sprint State */}
        {!displaySprint && !sprintId && (
          <Box className="no-sprint-state">
            <Box className="no-sprint-content">
              <img
                src="/sprint-placeholder.svg"
                alt="No active sprint"
                className="placeholder-image"
              />
              <Typography variant="h5" className="title">
                No Active Sprint
              </Typography>
              <Typography variant="body1" color="text.secondary" className="description">
                Create a new sprint to start planning and tracking your team's work
              </Typography>
              {canManageBoard && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleCreateSprint}
                  className="create-button"
                >
                  Create Sprint
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Sprint Content Tabs */}
        {displaySprint && (
          <>
            <Box className="sprint-tabs-container">
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                className="sprint-tabs"
              >
                <Tab
                  label="Board"
                  icon={<ViewKanbanIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Overview"
                  icon={<DescriptionIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Insights"
                  icon={<InsightsIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Sprint Burndown"
                  icon={<TrendingDownIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Backlog"
                  icon={<ListAltIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {/* Board Tab */}
            <TabPanel value={currentTab} index={0}>
              <SprintKanban sprintId={displaySprint.id} />
            </TabPanel>

            {/* Overview Tab */}
            <TabPanel value={currentTab} index={1}>
              <SprintOverviewCard sprintId={displaySprint.id} />
            </TabPanel>

            {/* Insights Tab */}
            <TabPanel value={currentTab} index={2}>
              <AgileInsights
                boardId={boardId}
                sprintId={displaySprint.id}
              />
            </TabPanel>

            {/* Sprint Burndown Tab */}
            <TabPanel value={currentTab} index={3}>
              <BurndownChart sprintId={displaySprint.id} />
            </TabPanel>

            {/* Backlog Tab */}
            <TabPanel value={currentTab} index={4}>
              <SprintBacklog sprintId={displaySprint.id} />
            </TabPanel>
          </>
        )}
      </Container>

      {/* Floating Action Button */}
      {canManageBoard && (
        <Tooltip title="Create New Sprint" placement="left">
          <Fab
            color="primary"
            className="create-fab"
            onClick={handleCreateSprint}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      )}

      {/* Modals */}
      <SprintCreationModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        boardId={boardId}
      />

      {displaySprint && (
        <CompleteSprintDialog
          open={completeDialogOpen}
          onClose={handleCloseCompleteDialog}
          sprint={displaySprint}
          incompleteTasks={incompleteTasks}
        />
      )}
    </Box>
  );
};

export default SprintManagementView;
